import type { ReactNode } from "react";
import { Badge } from "@mantine/core";
import { useLayout } from "./useLayout";

/**
 * A pill that reports rather than acts.
 *
 * Deliberately unable to take an onClick. The type is the point: the live pill is a `Chip`
 * when it will take you somewhere and a `StatePill` when it is telling you where you already
 * are, and nothing about the way it is written lets those two swap by accident.
 *
 * Not focusable either, for the same reason -- there is nothing to activate, and a tab stop
 * that does nothing when pressed is a worse lie than a mouse-only one. Mantine's `Badge` is a
 * `<div>` with no handlers, which is exactly that guarantee spelled in someone else's code.
 */
export function StatePill({
  tone,
  title,
  children,
}: {
  /** `live` is the danger hue, reserved for things happening now. */
  tone: "live" | "quiet";
  title?: string;
  children: ReactNode;
}) {
  // A finger is not a pointer, same floor a `Chip` gets. Inside the component rather than in
  // the prop union, which is deliberately closed.
  const { coarse } = useLayout();
  return (
    <Badge
      mih={coarse ? 36 : undefined}
      title={title}
      color={tone === "live" ? "danger" : "gray"}
      variant={tone === "live" ? "filled" : "light"}
    >
      {children}
    </Badge>
  );
}
